import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Memoized fetch to satisfy effect deps
  const fetchInvitationDetails = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/crew/invitations/${token}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid or expired invitation');
      }
      const data = await response.json();
      console.log('Invitation details:', data);

      if (!data.userExists) {
        // Redirect to register if user doesn't exist
        navigate(`/register?token=${token}&email=${encodeURIComponent(data.email)}`);
        return;
      }

      setInvitationData({ ...data, token });
      
      // Pre-fill email if provided
      if (data.email) {
        setFormData(prev => ({ ...prev, email: data.email }));
      }
    } catch (error) {
      console.error('Error fetching invitation:', error);
      setError(error.message || 'Invalid or expired invitation link');
    }
  }, [navigate]);

  // Get invitation data from location state or query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    if (token) {
      fetchInvitationDetails(token);
    }

    if (email) {
      setFormData(prev => ({ ...prev, email }));
    } else {
      // Load remembered email if present
      const remembered = localStorage.getItem('rememberedEmail');
      if (remembered) {
        setFormData(prev => ({ ...prev, email: remembered }));
        setRememberMe(true);
      }
    }
  }, [location, fetchInvitationDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
    // Basic per-field validation
    setFieldErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const validateForm = () => {
    const errs = { email: '', password: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      errs.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errs.email = 'Enter a valid email';
    }
    if (!formData.password) {
      errs.password = 'Password is required';
    }
    setFieldErrors(errs);
    return !errs.email && !errs.password;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Client-side validation
      const ok = validateForm();
      if (!ok) {
        setLoading(false);
        return;
      }
      // Use the login function from AuthContext
      const { token, user: userData } = await login(formData.email, formData.password);
      console.log('Login successful:', { token, user: userData });

      // Remember email preference
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      // If this was from an invitation, accept it
      if (invitationData?.token) {
        console.log('Accepting invitation with token:', invitationData.token);
        try {
          const acceptResponse = await fetch(`${API_BASE_URL}/api/v1/crew/invitations/${invitationData.token}/accept`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (!acceptResponse.ok) {
            const errorData = await acceptResponse.json();
            console.error('Failed to accept invitation:', errorData);
            throw new Error(errorData.message || 'Failed to accept invitation');
          }

          const acceptData = await acceptResponse.json();
          console.log('Invitation accepted:', acceptData);
          
          // Navigate to the project
          navigate(`/projects/${acceptData.projectId}`);
          return;
        } catch (acceptError) {
          console.error('Error accepting invitation:', acceptError);
          // Even if invitation acceptance fails, we're still logged in
          // So navigate to dashboard but show the error
          setError(`Logged in successfully but failed to accept invitation: ${acceptError.message}`);
          const redirectTo = location.state?.from?.pathname || '/dashboard';
          navigate(redirectTo);
          return;
        }
      }

      // If no invitation, redirect back or to dashboard
      const redirectTo = location.state?.from?.pathname || '/dashboard';
      navigate(redirectTo);
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      background: (theme) => `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`
    }}>
      <Container component="main" maxWidth="sm">
        <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography component="h1" variant="h5" gutterBottom>
              {invitationData ? 'Accept Invitation & Sign In' : 'Welcome back'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please enter your credentials to continue
            </Typography>
          </Box>

          {invitationData && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You've been invited to join {invitationData.projectName} as a {invitationData.role}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus={!formData.email}
              value={formData.email}
              onChange={handleChange}
              disabled={Boolean(invitationData?.email) || loading}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    color="primary"
                  />
                }
                label="Remember me"
              />
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : invitationData ? (
                'Accept & Sign In'
              ) : (
                'Sign In'
              )}
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Don't have an account?
              </Typography>
              <Link component={RouterLink} to="/register" variant="body2">
                Create one
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
